if (!customElements.get('dynamic-pricing-select')) {
  customElements.define(
    'dynamic-pricing-select',
    class DynamicPricingSelect extends HTMLElement {
      constructor() {
        super();

        this.controller = null;
        this.discount = Number(this.dataset.disc) || 0;
        this.isOnSale = this.discount > 0;
        this.quantity = 1;
        this.variantId = this.dataset.variantId;
        this.params = Number(new URLSearchParams(window.location.search).get('days'));
        const days = Number(this.params) || window.MIN_DAYS;
        this.maxDays = window.MAX_DAYS || window.pricing?.[this.dataset.category]?.length || 180;
        this.days = Math.min(this.maxDays, Math.max(window.MIN_DAYS, days));

        this.handleSelectClick = this.handleSelectClick.bind(this);
        this.handleCloseClick = this.handleCloseClick.bind(this);
        this.handleOverlayClick = this.handleOverlayClick.bind(this);
        this.handleEscapeKey = this.handleEscapeKey.bind(this);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleDaySelectBound = this.handleDaySelect.bind(this);
        this.handleQuantityChangeBound = this.handleQuantityChangeBound.bind(this);
        this.handleQuantityResetBound = this.handleQuantityResetBound.bind(this);
      }

      connectedCallback() {
        this.select = this.querySelector('.days-select');
        this.trigger = this.closest('.select-days');
        this.list = this.querySelector('ul');
        this.overlay = this.querySelector('.days-select-overlay');
        this.panel = this.querySelector('.days-select-modal');
        this.swipeHandle = this.querySelector('.days-select-header');
        this.closeBtn = this.querySelector('.days-select-close');
        this.allDaysList = this.querySelector('.days-select-list ul');

        if (this.select) {
          this.select.querySelector('p').innerHTML = `${this.days} Day${this.days > 1 ? 's' : ''}`;
        }
        const init = () => {
          this.calculatePrice();
          this.updateProductFormInputs();
          this.bindBacksheetController();

          this.trigger?.addEventListener('click', this.handleSelectClick);
          if (!this.controller) {
            this.closeBtn?.addEventListener('click', this.handleCloseClick);
            this.overlay?.addEventListener('click', this.handleOverlayClick);
            document.addEventListener('keydown', this.handleEscapeKey);
          }
          document.addEventListener('click', this.handleDocumentClick);

          this.handleQuantityChange();
          this.displayOptions();
        };

        this.initCallback = init;
        document.readyState === 'loading'
          ? document.addEventListener('DOMContentLoaded', this.initCallback)
          : init();
      }

      updateQueryStringParam() {
        const toggleProducts = document.querySelector('toggle-products');
        const isFixedActive = !!toggleProducts?.querySelector('button.fixed__btn.active');

        if (!isFixedActive) {
          const searchParams = new URLSearchParams(window.location.search);
          searchParams.set('days', this.days);
          history.replaceState(null, null, `?${searchParams.toString()}`);
        }
      }

      isMobile() {
        return window.matchMedia('(max-width: 768px)').matches;
      }

      bindBacksheetController() {
        if (this.controller || !window.createBacksheetController || !this.overlay || !this.panel) {
          return;
        }

        this.controller = window.createBacksheetController({
          host: this,
          overlay: this.overlay,
          panel: this.panel,
          closeBtn: this.closeBtn,
          swipeHandle: this.swipeHandle,
          visibleClass: 'active',
          moveBackDelay: 240,
          lock: () => {
            window.scrollLock?.lock();
          },
          unlock: () => {
            window.scrollLock?.unlock();
          },
          shouldEnableSwipe: () => this.isMobile(),
          onOpen: () => {
            this.overlay.classList.remove('closing');
            this.classList.add('is-open');
            requestAnimationFrame(() => {
              this.scrollSelectedOptionIntoView();
            });
          },
          onClose: () => {
            this.overlay.classList.remove('closing');
            this.classList.remove('is-open');
          },
        });

        this.controller.bind();
      }

      displayOptions() {
        const allFragment = document.createDocumentFragment();
        const popularDays = Number(this.dataset.pop);

        let i = 1;
        while (i <= this.maxDays) {
          const li = document.createElement('li');
          li.value = i;
          li.innerHTML = `${i}${` Day${i > 1 ? 's' : ''}`}`;
          li.classList.add('dp-option');
          li.value === this.days && li.classList.add('selected');
          allFragment.appendChild(li);
          i++;
        }

        this.allDaysList.appendChild(allFragment);

        this.options = this.querySelectorAll('.dp-option');
        this.options.forEach((option) => {
          option.addEventListener('click', this.handleDaySelectBound);
          if (popularDays > 0 && popularDays === Number(option.value)) {
            option.classList.add('popular');
          }
        });
      }

      handleSelectClick(event) {
        const clickTarget = event.target instanceof Element ? event.target : null;

        if (this.overlay?.contains(clickTarget) || clickTarget?.closest('.days-select-close')) {
          return;
        }

        if (this.classList.contains('is-open')) {
          this.closePopup();
        } else {
          this.openPopup();
        }
      }

      handleDocumentClick(e) {
        if (!this.contains(e.target)) {
          this.closePopup();
        }
      }

      handleCloseClick() {
        this.closePopup();
      }

      handleOverlayClick(e) {
        if (e.target === this.overlay) {
          this.closePopup();
        }
      }

      handleEscapeKey(e) {
        if (e.key === 'Escape') {
          this.closePopup();
        }
      }

      handleDaySelect(e) {
        this.options.forEach((option) => {
          option.classList.remove('selected');
          if (e.target.value === option.value) {
            option.classList.add('selected');
          }
        });
        this.days = Number(e.target.value);
        this.select.querySelector('p').innerHTML =
          `${this.days}${` Day${Number(this.days) > 1 ? 's' : ''}`}`;
        this.calculatePrice();
        this.updateProductFormInputs();

        clearTimeout(this.closeTimeout);
        this.closeTimeout = setTimeout(() => {
          this.closePopup();
        }, 150);
      }

      scrollSelectedOptionIntoView() {
        if (!this.list) return;
        const selectedOption =
          this.list.querySelector('.dp-option.selected') ||
          this.list.querySelector(`.dp-option[value="${this.days}"]`);

        if (!selectedOption) return;

        const listRect = this.list.getBoundingClientRect();
        const optionRect = selectedOption.getBoundingClientRect();
        const offset = optionRect.top - listRect.top;
        const targetScroll =
          this.list.scrollTop +
          offset -
          (this.list.clientHeight / 2 - selectedOption.clientHeight / 2);

        this.list.scrollTop = Math.max(0, targetScroll);
      }

      openPopup() {
        if (this.isMobile() && this.controller) {
          this.controller.open();
          return;
        }

        this.overlay.classList.remove('closing');
        this.overlay.classList.remove('active');
        // Allow initial styles to apply before transitioning.
        requestAnimationFrame(() => {
          this.overlay.classList.add('active');
          // if (this.isMobile()) {
          requestAnimationFrame(() => {
            this.scrollSelectedOptionIntoView();
          });
          // }
        });
        this.classList.add('is-open');
      }

      closePopup() {
        if (!this.overlay.classList.contains('active')) return;

        if (this.isMobile() && this.controller) {
          this.controller.close();
          return;
        }

        this.overlay.classList.remove('active');
        this.overlay.classList.add('closing');
        this.classList.remove('is-open');

        clearTimeout(this.closeAnimationTimeout);
        this.closeAnimationTimeout = setTimeout(() => {
          this.overlay.classList.remove('closing');
        }, 240);
      }

      handleQuantityChange() {
        this.quantitySelector = document.querySelector('quantity-input input[name="quantity"]');

        if (!this.quantitySelector) return;

        this.quantitySelector.addEventListener('change', this.handleQuantityChangeBound);
        this.quantitySelector.addEventListener('inputReset', this.handleQuantityResetBound);
      }

      handleQuantityChangeBound() {
        this.quantity = Number(this.quantitySelector?.value || 1);
        this.calculatePrice();
      }

      handleQuantityResetBound() {
        this.quantity = 1;
        this.calculatePrice();
      }

      formatPrice(currency, val) {
        const formattedPrice = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol',
        });

        const formatted = formattedPrice.format(val).replace(/\.00$/, '');
        return currency === 'EUR' ? formatted.replace('.', ',') : formatted;
      }

      calculatePrice() {
        this.updateQueryStringParam();

        const totalPriceContainer = document.querySelector('.total-dp-price');
        const price = getPrice(this.days);

        if (!price || !totalPriceContainer) return;
        totalPriceContainer.innerHTML = `${window.cartCurrency.slice(0, 2)}${this.formatPrice(window.cartCurrency, price * this.quantity)}`;

        this.calculatePerDayPrice(price);

        if (this.isOnSale) {
          const salePriceSpan = document.querySelector('.total-dp-price-sale');
          const salePrice = this.formulateSalePrice(price);
          if (salePriceSpan) {
            salePriceSpan.innerHTML = `${window.cartCurrency.slice(0, 2)}${this.formatPrice(window.cartCurrency, salePrice * this.quantity)}`;
          }
        }

        // Update JSON-LD schema to match current price and URL
        // this.updateSchema(price);
      }

      updateSchema(price) {
        const schemaEl = document.querySelector('script.universal-product-schema');
        if (!schemaEl) return;

        try {
          const data = JSON.parse(schemaEl.textContent);
          if (!data || !data.offers) return;

          // Update price (unit price for 1) and currency
          data.offers.price = price;
          data.offers.priceCurrency = window.cartCurrency;

          // Update URL to include correct days parameter
          const currentUrl = new URL(data.offers.url);
          currentUrl.searchParams.set('days', this.days);

          history.pushState(null, null, currentUrl.toString());

          data.offers.url = currentUrl.toString();

          schemaEl.textContent = JSON.stringify(data);
        } catch (e) {
          // Silently fail if schema is malformed
        }
      }

      formulateSalePrice(price, perDay = false) {
        let amountOff = price * (this.discount / 100);
        amountOff = Math.floor(amountOff * 100) / 100;
        if (perDay) {
          return Math.round(((price - amountOff) / this.days) * 100) / 100;
        } else {
          return Math.round((price - amountOff) * 100) / 100;
        }
      }

      calculatePerDayPrice(totalPrice) {
        const perDayPriceContainers = document.querySelectorAll('.perday-dp-price');
        // const perDayPriceSaleContainers = document.querySelectorAll('.perday-dp-price-sale');

        if (!perDayPriceContainers.length) return;
        let perDayPrice;
        if (this.isOnSale) {
          perDayPrice = this.formulateSalePrice(totalPrice, true);
        } else {
          perDayPrice = Math.round((totalPrice / this.days) * 100) / 100;
        }

        const perDayLabel = `${this.formatPrice(window.cartCurrency, perDayPrice)}/${this.quantity > 1 ? 'eSIM/' : ''}day`;

        perDayPriceContainers.forEach((container) => {
          container.innerHTML = perDayLabel;
        });
      }

      updateProductFormInputs() {
        const variantIdInput = document.querySelector('input[name="id"]');
        const dpDaysInput = document.querySelector('input[name="properties[dp-days]"]');

        if (variantIdInput && dpDaysInput) {
          variantIdInput.value = this.variantId;
          dpDaysInput.value = this.days;
        }
      }

      disconnectedCallback() {
        clearTimeout(this.closeTimeout);
        clearTimeout(this.closeAnimationTimeout);

        if (this.initCallback) {
          document.removeEventListener('DOMContentLoaded', this.initCallback);
        }

        this.select?.removeEventListener('click', this.handleSelectClick);
        if (!this.controller) {
          this.closeBtn?.removeEventListener('click', this.handleCloseClick);
          this.overlay?.removeEventListener('click', this.handleOverlayClick);
          document.removeEventListener('keydown', this.handleEscapeKey);
        }
        document.removeEventListener('click', this.handleDocumentClick);

        if (this.options?.length) {
          this.options.forEach((option) => {
            option.removeEventListener('click', this.handleDaySelectBound);
          });
        }

        if (this.quantitySelector) {
          this.quantitySelector.removeEventListener('change', this.handleQuantityChangeBound);
          this.quantitySelector.removeEventListener('inputReset', this.handleQuantityResetBound);
        }

        this.controller?.unbind();

        if (this.overlay?.parentElement === document.body) {
          this.appendChild(this.overlay);
        }
      }
    },
  );
}
